// src/features/scheduleGenerator/utils/planningSessionManager.ts

import { logger } from '../../../utils/logger';

import { ToneOption, SkeletonTimeBlock } from '../services/scheduleGeneratorService';

interface PlanningSession {
  lastSummary: string;
  latestNote: string;
  intention?: string;
  skeletonTimeBlocks?: SkeletonTimeBlock[];
  toneOptions?: ToneOption[];
}

const STORAGE_PREFIX = 'aiplan_session_';

function getStorageKey(userId: string, date: string): string {
  return `${STORAGE_PREFIX}${userId}_${date}`;
}

function readSession(userId: string, date: string): PlanningSession {
  try {
    const raw = localStorage.getItem(getStorageKey(userId, date));
    if (!raw) return { lastSummary: '', latestNote: '' };
    const parsed = JSON.parse(raw) as Partial<PlanningSession>;
    return {
      lastSummary: parsed.lastSummary || '',
      latestNote: parsed.latestNote || '',
      intention: parsed.intention,
      skeletonTimeBlocks: parsed.skeletonTimeBlocks,
      toneOptions: parsed.toneOptions,
    };
  } catch {
    localStorage.removeItem(getStorageKey(userId, date));
    return { lastSummary: '', latestNote: '' };
  }
}

function writeSession(userId: string, date: string, session: PlanningSession): void {
  try {
    localStorage.setItem(getStorageKey(userId, date), JSON.stringify(session));
  } catch (error) {
    logger.error('PlanningSessionManager', '寫入 session 失敗:', error);
  }
}

export const PlanningSessionManager = {
  setLatestNote(userId: string, date: string, note: string): void {
    const trimmed = note.trim();
    if (!trimmed) return;
    const session = readSession(userId, date);
    writeSession(userId, date, { ...session, latestNote: trimmed });
  },

  getLatestNote(userId: string, date: string): string {
    return readSession(userId, date).latestNote;
  },

  setLastSummary(userId: string, date: string, summary: string): void {
    const session = readSession(userId, date);
    writeSession(userId, date, { ...session, lastSummary: summary });
  },

  getLastSummary(userId: string, date: string): string {
    return readSession(userId, date).lastSummary;
  },

  clearSession(userId: string, date: string): void {
    try {
      localStorage.removeItem(getStorageKey(userId, date));
    } catch (error) {
      logger.error('PlanningSessionManager', '清除 session 失敗:', error);
    }
  },

  // Day Draft V3 session fields
  setTone(userId: string, date: string, intention: string, options?: ToneOption[]): void {
    const session = readSession(userId, date);
    writeSession(userId, date, { ...session, intention, toneOptions: options });
  },

  getTone(userId: string, date: string): { intention: string; options?: ToneOption[] } | null {
    const session = readSession(userId, date);
    if (!session.intention) return null;
    return { intention: session.intention, options: session.toneOptions };
  },

  setSkeleton(userId: string, date: string, timeBlocks: SkeletonTimeBlock[]): void {
    const session = readSession(userId, date);
    writeSession(userId, date, { ...session, skeletonTimeBlocks: timeBlocks });
  },

  getSkeleton(userId: string, date: string): SkeletonTimeBlock[] | null {
    const session = readSession(userId, date);
    return session.skeletonTimeBlocks ?? null;
  },

  clearToneAndSkeleton(userId: string, date: string): void {
    const session = readSession(userId, date);
    const { intention: _intention, skeletonTimeBlocks: _s, toneOptions: _o, ...rest } = session;
    writeSession(userId, date, rest as PlanningSession);
  },
};
